import { Box, BoxProps, TabItem, TabView, Toggle, ToggleGrid } from '@wrisk/ui-components'
import React, { ChangeEvent, FunctionComponent, useCallback, useState } from 'react'

import { Quote } from '../../../domain'
import { useDiscountCovers } from '../../../hooks/covers'
import { useQuotePremiums } from '../../../hooks/premiums'
import { TKey, useWriskTranslation } from '../../../infrastructure/internationalisation'
import { isPaymentScheduleType, ProductConfig } from '../../../state/configuration'
import { usePaymentSchedule } from '../../product/paymentScheduleContext'
import { StandardAdjustedPremium } from './StandardAdjustedPremium'
import { StandardAdjustedPremiumMonthly } from './StandardAdjustedPremiumMonthly'
import { StandardAdjustedPremiumYearly } from './StandardAdjustedPremiumYearly'

export interface AnnualAdjustedPremiumsProps extends BoxProps {
  currentQuote: Quote
  previousQuote?: Quote
  product: ProductConfig
  expanded?: boolean
  toggleable?: boolean
}

const tKey = TKey('components.annual-adjusted-premium')

export const AnnualAdjustedPremium: FunctionComponent<AnnualAdjustedPremiumsProps> = ({
  currentQuote,
  previousQuote,
  product,
  expanded,
  toggleable = true,
  ...props
}) => {
  const { t } = useWriskTranslation()

  const { paymentScheduleType, setPaymentScheduleType } = usePaymentSchedule()

  const paymentTypes = product.payment?.schedules.map(({ type }) => type)
  const initialTab = paymentTypes?.findIndex((it) => it === paymentScheduleType) ?? 0
  const [tab, setTab] = useState(initialTab)

  const currentPremiums = useQuotePremiums(currentQuote, product)
  const previousPremiums = useQuotePremiums(previousQuote, product)

  const [discount] = useDiscountCovers(currentQuote)

  const [isExpanded, setIsExpanded] = useState(expanded ?? false)
  const onExpansionChange = useCallback(setIsExpanded, [expanded])

  const onToggle = useCallback(({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    if (isPaymentScheduleType(value)) {
      setTab(paymentTypes?.indexOf(value) ?? 0)
      setPaymentScheduleType(value)
    } else {
      throw new Error(`No payment schedule of type ${value}`)
    }
  }, [])

  const items: TabItem[] =
    paymentTypes?.map((type) => {
      switch (type) {
        case 'MONTHLY':
          return {
            component: () => (
              <StandardAdjustedPremiumMonthly
                currentQuote={currentQuote}
                previousQuote={previousQuote}
                product={product}
                discount={discount}
                expanded={isExpanded}
                tKey={tKey}
                onExpansionChange={onExpansionChange}
              />
            ),
          }
        case 'SINGLE_PAYMENT':
          return {
            component: () => (
              <StandardAdjustedPremiumYearly
                currentQuote={currentQuote}
                previousQuote={previousQuote}
                product={product}
                discount={discount}
                expanded={isExpanded}
                tKey={tKey}
                onExpansionChange={onExpansionChange}
              />
            ),
          }
      }
    }) ?? []

  return (
    <Box width={1} p={2} {...props}>
      {paymentTypes ? (
        <>
          {paymentTypes.length > 1 && toggleable && (
            <Box width={1} px={3} pt={3} pb={2}>
              {/* Workaround for the same input ids in one form (we have two ProposalSidebars)  */}
              <form autoComplete='off'>
                <ToggleGrid width={1} columns={2}>
                  {paymentTypes.map((it) => (
                    <Toggle
                      name='paymentToggle'
                      key={it}
                      id={it}
                      value={it}
                      defaultChecked={it === paymentScheduleType}
                      data-testid={`payment-type-${it}`}
                      onChange={onToggle}
                    >
                      {t(tKey('toggle', it))}
                    </Toggle>
                  ))}
                </ToggleGrid>
              </form>
            </Box>
          )}
          <TabView headless width={1} items={items} tabIndex={tab} onTabChange={setTab} />
        </>
      ) : (
        <StandardAdjustedPremium
          currentPremiums={currentPremiums}
          previousPremiums={previousQuote && previousPremiums}
          discount={discount}
          expanded={expanded}
          tKey={tKey}
        />
      )}
    </Box>
  )
}
